<script>
/* eslint no-multi-spaces: 0 */
/* eslint key-spacing    : 0 */

/**
 * SETTINGS MODULE
 *
 * This module handles settings for other modules based on user roles.
 *
 * @package Admin
 */

import { mixin as clickaway } from 'vue-clickaway'

import Vue       from 'vue'
import user      from '@/core/user'
import endpoints from '@/core/api/map.json'
import mapValues from 'lodash/mapValues'
import orderBy   from 'lodash/orderBy'
import VuePluralize from 'vue-pluralize'
import LegacyAuth from '@/core/legacy-auth'

const { allowedModules } = LegacyAuth

Vue.use(VuePluralize)

let cards  = []
let entity = null
let role   = null

const init = function() {
  let _settings = null
  let _data     = null

  if (user.get() && user.get().data/* && user.get().settings */) {
    _settings = user.getCompanySettings()
    _data     = user.get().data

    _data.MarketTypeID           = parseInt(_data.MarketTypeID)
    _data.MembershipPlanID       = parseInt(_data.MembershipPlanID)
    _data.ApplyPartnerCommission = user.get().hasPC
    _data.IsDell                 = user.get().isDell
  }

  const User = mapValues(Object.assign({ settings: _settings }, _data), toBool)

  const ROLE = {
    BASIC          : User.type === 'basic',
    NO_ADMIN       : true,
    REGIONAL_ADMIN : allow('RegionalAdministration'),
    COMPANY_ADMIN  : allow('Administration'),
    FULL_ADMIN     : User.type === 'admin'
  }

  const type = {
    BUYER    : User.MarketTypeID === 1,
    BS       : User.MarketTypeID === 2,
    SELLER   : User.MarketTypeID === 3
  }

  const ENTITY = {
    ENTERPRISE    : User.IsE360,
    CORPORATE     : User.IsFusion,
    SUPPLIER      : type.BS || type.SELLER,

    BASIC_BS      : type.BS    &&            !User.IsE360 && !User.IsFusion,
    ENTERPRISE_BS : type.BS    &&             User.IsE360 &&  User.MembershipPlanID === 3,
    CORPORATE_B   : type.BUYER &&             User.IsFusion,
    BASIC_B       : type.BUYER && !User.IsFusion
  }

  const SETTINGS = {
    COMPANY: Object.assign({
      //
      _pages: mapValues(endpoints.settings.company, page => `#/i/Admin${page}[back]#Settings`)
    },
      //
      mapValues(User.settings, toBool)
    )
  }

  entity = ENTITY
  role   = ROLE

  //
  cards = [
    {
      name: 'Approval Settings',
      status: 'AllowApprovalSettings',
      description: 'Allow users to manage approval rules.',
      configurable: 'Configure',
      icon: ['far', 'check-circle'],
      new: true,
      url: '#/d/Admin/Approval-list[back]',
      endpoint: endpoints.settings.company.approval,
      condition: ENTITY.ENTERPRISE || ENTITY.CORPORATE
    },

    {
      name: 'Approval Delegation',
      status: 'AllowDelegateApproval',
      description: 'Allow users to delegate approvals.',
      configurable: 'false',
      icon: ['fab', 'telegram-plane'],
      new: false,
      condition: ENTITY.CORPORATE
    },

    {
      name: 'Time & Date',
      status: null,
      description: 'Set up time & date.',
      configurable: 'Configure',
      icon: ['far', 'clock'],
      new: false,
      url: SETTINGS.COMPANY._pages.timeDate
    },

    {
      name: 'Budget Settings',
      status: null,
      description: 'Customize your budget and VAT/GST settings.',
      configurable: 'Configure',
      icon: ['far', 'usd-circle'],
      new: false,
      url: SETTINGS.COMPANY._pages.budget,
      condition: ENTITY.CORPORATE
    },

    {
      name: 'Payments',
      status: null,
      description: 'Set up global payment settings.',
      configurable: 'Configure',
      icon: ['fas', 'credit-card'],
      new: false,
      url: SETTINGS.COMPANY._pages.payment,
      condition: ENTITY.CORPORATE && ROLE.COMPANY_ADMIN
    },

    {
      name: 'Tax Authority',
      status: null,
      description: 'Set up and configure Tax Authorities.',
      configurable: 'Configure',
      icon: ['fal', 'percent'],
      new: false,
      url: SETTINGS.COMPANY._pages.taxAuthority,
      condition: !ENTITY.CORPORATE && ENTITY.ENTERPRISE
    },

    {
      name: SETTINGS.COMPANY.ProgramLabel + ' Template',
      status: null,
      description: 'Set up and configure ' + SETTINGS.COMPANY.ProgramLabel + ' Templates.',
      configurable: 'Configure',
      icon: ['fas', 'file-alt'],
      new: false,
      url: SETTINGS.COMPANY._pages.programTemplate,
      condition: ENTITY.CORPORATE && !ROLE.REGIONAL_ADMIN
    },

    {
      name: 'Email Addresses for Reports',
      status: null,
      description: 'Email Addresses for Reports.',
      configurable: 'Configure',
      icon: ['far', 'envelope'],
      new: false,
      url: SETTINGS.COMPANY._pages.email,
      condition: ENTITY.ENTERPRISE || ENTITY.CORPORATE
    },

    {
      name: 'Scope Settings',
      status: null,
      description: 'Customize your Scope of Services settings.',
      configurable: 'Configure',
      icon: ['far', 'file-alt'],
      new: false,
      url: SETTINGS.COMPANY._pages.scope,
      condition: ENTITY.CORPORATE_B
    },

    {
      name: ENTITY.CORPORATE ? SETTINGS.COMPANY.ProgramsLabel : 'Program Settings',
      initial: SETTINGS.COMPANY.ProgramsLabel,
      status: null,
      editable: ENTITY.CORPORATE ? 'Rename Setting' : '',
      model: 'ProgramsLabel',
      placeholder: 'Program',
      description: `Customize your [placeholder] and Team Tab settings.`,
      configurable: 'Configure',
      icon: ['fas', 'file'],
      new: false,
      url: SETTINGS.COMPANY._pages.program,
      condition: (ENTITY.ENTERPRISE || ENTITY.CORPORATE) && !ROLE.REGIONAL_ADMIN
    },

    {
      name: SETTINGS.COMPANY.DivisionName || 'Divisions',
      initial: SETTINGS.COMPANY.DivisionName || 'Divisions',
      status: 'HasDivision',
      editable: 'Rename Setting',
      model: 'DivisionName',
      modelDescription: 'DivisionDescription',
      placeholder: 'Divisions',
      description: `Set up [placeholder] for your company to drive your approvals, reporting and user access.`,
      configurable: 'Configure',
      icon: ['fas', 'thumbs-up'],
      new: false,
      url: SETTINGS.COMPANY._pages.division,
      condition: !ROLE.REGIONAL_ADMIN && ENTITY.CORPORATE
    },

    {
      name: 'Business Units and Cost Centers',
      status: null,
      description: '                                       ',
      configurable: 'Configure',
      icon: ['fas', 'user'],
      new: false,
      url:'#/d/Admin/BusinessUnitsAndCostCenters[back]',
      condition: !ROLE.REGIONAL_ADMIN && ENTITY.CORPORATE
    },

    {
      name: 'Currencies',
      status: 'HasCurrency',
      description: 'Set up global currencies to be used in your ' + SETTINGS.COMPANY.ProgramsLabel.toLowerCase() + '.',
      help: 'Currencies selected here can then be configured as currencies to be processed as payments for invoices in the Payments configuration section.',
      configurable: 'Configure',
      icon: ['far', 'euro-sign'],
      new: false,
      url: SETTINGS.COMPANY._pages.currency,
      condition: !ROLE.REGIONAL_ADMIN && ENTITY.CORPORATE
    },

    {
      name: 'ERP',
      status: 'HasERP',
      description: 'Integrate with Enterprise Resource Planning (ERP) software.',
      configurable: 'Configure',
      icon: [],
      new: false,
      url: SETTINGS.COMPANY._pages.erp,
      condition: User.IsDell
    },

    {
      name: 'Expenses Customizations',
      status: 'HasExpense',
      description: 'Customize your Expense settings.',
      configurable: 'Configure',
      icon: ['fal', 'credit-card'],
      new: false,
      url: SETTINGS.COMPANY._pages.expense,
      condition: !ROLE.REGIONAL_ADMIN && ENTITY.CORPORATE
    },

    {
      name: 'Partners',
      status: 'HasPartnerFunding',
      description: 'Set up partners for funding allocations.',
      configurable: 'Configure',
      icon: ['far', 'handshake'],
      new: false,
      url: SETTINGS.COMPANY._pages.partner,
      condition: !ROLE.REGIONAL_ADMIN && ENTITY.CORPORATE
    },

    {
      name: 'Categories',
      status: null,
      description: 'Create and manage custom categories.',
      configurable: 'Configure',
      icon: ['fal', 'list-alt'],
      new: false,
      url: SETTINGS.COMPANY._pages.category,
      condition: ENTITY.CORPORATE
    },

    {
      name: 'Sub-Categories',
      status: 'HasSubCategory',
      description: 'Create and manage custom sub-categories.',
      configurable: 'Configure',
      icon: ['fal', 'list-alt'],
      new: false,
      url: SETTINGS.COMPANY._pages.category,
      condition: ENTITY.ENTERPRISE
    },

    {
      name: 'Partner Commissions',
      value: User.ApplyPartnerCommission,
      status: 'ApplyPartnerCommission',
      description: 'Set up partner commissions.',
      configurable: 'Configure',
      icon: ['far', 'handshake'],
      new: false,
      url: SETTINGS.COMPANY._pages.partnerCommission,
      condition: ENTITY.ENTERPRISE
    },

    {
      name: 'Invoices',
      status: 'HasInvoice',
      description: 'Set up invoice preferences for approvals, terms and rush payments.',
      configurable: 'Configure',
      icon: ['far', 'file-alt'],
      new: false,
      url: SETTINGS.COMPANY._pages.invoice,
      condition: (ENTITY.CORPORATE || ENTITY.ENTERPRISE_BS) && !ROLE.REGIONAL_ADMIN
    },

    {
      name: SETTINGS.COMPANY.Labels.MeetingTypesLabel,
      initial: SETTINGS.COMPANY.Labels.MeetingTypesLabel,
      status: 'UseMeetingType',
      editable: 'Rename Setting',
      model: 'MeetingTypesLabel',
      placeholder: 'Meeting Types',
      description: 'Track [placeholder] per ' + SETTINGS.COMPANY.ProgramLabel.toLowerCase() + ' and customize your list of [placeholder].',
      configurable: 'Configure',
      icon: [],
      new: false,
      url: SETTINGS.COMPANY._pages.meetingType,
      condition: ENTITY.CORPORATE && !ROLE.REGIONAL_ADMIN
    },

    {
      name: 'Purchase Orders',
      status: 'HasPurchaseOrder',
      description: 'Set up terms, preferences and thresholds within the approval work flow.',
      configurable: 'Configure',
      icon: ['far', 'shopping-cart'],
      new: false,
      url: SETTINGS.COMPANY._pages.po,
      condition: (ENTITY.CORPORATE || ENTITY.ENTERPRISE_BS) && !ROLE.REGIONAL_ADMIN
    },

    {
      name: 'Invitation Settings',
      status: null,
      description: 'Choose whether or not you want to automatically accept buyer invitations.',
      configurable: 'Configure',
      icon: ['fas', 'users'],
      new: false,
      url: SETTINGS.COMPANY._pages.invitation,
      condition: ENTITY.SUPPLIER
    },

    {
      name: 'Notifications',
      status: null,
      description: 'Set up company notifications.',
      configurable: 'Configure',
      icon: ['far', 'bell'],
      new: false,
      url: ENTITY.ENTERPRISE_BS ? SETTINGS.COMPANY._pages.companyNotification : SETTINGS.COMPANY._pages.notification,
      condition: (SETTINGS.COMPANY.HasNotifications && SETTINGS.COMPANY.HasInsuranceModule) || !ENTITY.CORPORATE
    },

    {
      name: 'Templates',
      status: null,
      description: 'Set up proposal templates.',
      configurable: 'Configure',
      icon: ['far', 'shopping-cart'],
      new: false,
      url: SETTINGS.COMPANY._pages.template,
      condition: ENTITY.ENTERPRISE
    },

    {
      name: 'VAT/GST',
      status: 'HasVAT',
      description: 'Set up VAT/GST Identification Numbers.',
      configurable: 'Configure',
      icon: [],
      new: false,
      url: SETTINGS.COMPANY._pages.vat,
      condition: User.IsDell
    },

    {
      name: 'Landmarks',
      status: 'UseLandmarks',
      description: 'Add custom locations to allow Distance From searches in the supplier network.',
      configurable: 'Configure',
      icon: ['fal', 'map-pin'],
      new: false,
      url: SETTINGS.COMPANY._pages.landmark,
      condition: ENTITY.ENTERPRISE || ENTITY.CORPORATE
    },

    {
      name: 'Accounts Payable Settings',
      status: null,
      description: 'Configure funding addresses and AP File templates.',
      configurable: 'Configure',
      icon: ['far', 'check-circle'],
      new: false,
      url:'#/d/Admin/FundingInvoiceAndAPFileTemplate[back]',
      endpoint: endpoints.settings.company.fundingInvAndAPSettings,
      condition: ENTITY.CORPORATE && User.HasAuditFile
    }
  ]

  cards = orderBy(cards, [card => card.name.toLowerCase()], ['asc'])

  return SETTINGS.COMPANY
}

function allow(roleName) {
  return !~getPermissions().indexOf('deny' + roleName)
}

function toBool(v) {
  return v === '1' ? true : v === '0' ? false : v
}

function getPermissions() {
  const authUser = JSON.parse(window.localStorage.getItem('evedUser'))
  var permissions = []

  if (authUser && authUser.token && authUser.permissions) {
    permissions = authUser.permissions
  }

  return permissions
}

export default {
  name: 'Settings',

  mixins: [ clickaway ],

  created() {
    this.updateSettings()

    if (!role.COMPANY_ADMIN || entity.BASIC_B) {
      this.$router.push('/i/Admin/GeneralSettings/GeneralSettings')
    }
  },

  data () {
    return {
      settings: cards,

      isDisabled: (setting) => {
        if (setting.status === null) return false
        return typeof setting.value === 'boolean'
          ? !setting.value
          : !this.$user.$.companySettings[setting.status]
      },

      backUrl: (url, setting) => {
        return setting.status !== null && !setting.value
          ? 'javascript:;'
          : url ? url.replace('[back]', '?back-to=Company Settings') : ''
      },

      subtitle: (setting) => !setting.editing
        ? setting.description.replace(/\[placeholder\]/g, setting.name || setting.placeholder)
        : setting.description.replace(/\[placeholder\]/g, setting.initial || setting.placeholder)
    }
  },

  methods: {
    edit(setting, hide) {
      if (!setting.editable || this.isDisabled(setting)) return
      if (hide === false && setting.editing === true) {
        setting.name = setting.name + ' '
        setTimeout(() => {
          setting.name = setting.initial // setting.name.slice(0, -1)
          setting.editing = false
        })
      } else {
        setting.name = setting.name + ' '
        setTimeout(() => {
          setting.name = setting.initial // setting.name.slice(0, -1)
          setting.editing = true
        })
      }
    },

    handleEndpoint(setting, e) {
      if (e.button === 0 && ~e.target.className.indexOf('btn-primary')) {
        setting.saving = true
      }
    },

    change(setting, hide) {
      const vm = this

      const payload = {
        isAllowed: setting.value
      }

      if (setting.description.indexOf('Team Tab') !== -1 ||
        setting.model === 'MeetingTypesLabel') {
        // pluralize Programs label
        setting.name = global.app.$pluralize(setting.name.trim(), 2)
      }

      const oldSettingsPayload = {
        SaveType: 'settings',
        [setting.status]: setting.value
      }

      if (setting.model) {
        oldSettingsPayload[setting.model] = setting.name
        if (setting.modelDescription) oldSettingsPayload[setting.modelDescription] = ''
      }

      //
      if (!setting.value && setting.status) {
        setting.pending = true

        global.app.$notifications.notify({
          type: 'plain',
          icon: 'exclamation-circle',
          placement: { from: 'top', align: 'center' },
          timeout: 0,
          message: `Are you sure you want to turn off your admin settings for <strong>${setting.name}</strong> ?` +
                    `<br/>` +
                    `This will immediately turn off these settings in your company's account.`,
          dismiss: this.$t('Yes'),
          discard: this.$t('No'),
          goto: null,
          onClose: function (dismiss) {
            if (dismiss) {
              //
              save(vm, dismiss)
            } else {
              //
              setting.value = true
            }

            setting.pending = false
          }
        })
      } else {
        replaceProgramLabelInTabs(this.settings, setting)
        updateSessionSettings(setting.name, setting.model, this)

        save(this)
      }

      function updateSessionSettings(newKey, model, _this) {
        if (model === 'ProgramsLabel' || model === 'MeetingTypesLabel') {
          let data = JSON.parse(window.localStorage.getItem('evedUser'))

          if (data.modules && data.modules.Commerce && newKey) {
            let newObj = {}
            let oldKey = newKey
            Object.keys(data.modules.Commerce).map(k => {
              if (data.modules.Commerce[k] === 'ProgramList') {
                newObj[newKey] = data.modules.Commerce[k]
                oldKey = k
              } else {
                newObj[k] = data.modules.Commerce[k]
              }
            })

            if (newKey !== oldKey) {
              data.modules.Commerce = newObj

              _this.$nextTick(() => {
                global.localStorage.setItem('evedUser', JSON.stringify(data))

                // update cache
                allowedModules()
              })
            }
          }
        }
      }

      function replaceProgramLabelInTabs(allSettings, menuSetting) {
        if (menuSetting.description && menuSetting.description.indexOf('Team Tab') !== -1) {
          let programLabel = global.app.$pluralize(menuSetting.name, 1) // singular
          let programsLabel = global.app.$pluralize(menuSetting.name, 2) // plural

          allSettings.map(s => {
            // Program Template
            if (s.name && s.name.indexOf('Template') !== -1) {
              s.name = programLabel + ' Template'
              s.description = 'Set up and configure ' + programLabel + ' Templates.'
            }

            // Currencies
            if (s.name && s.name.indexOf('Currencies') !== -1) {
              s.description = 'Set up global currencies to be used in your ' + programsLabel.toLowerCase() + '.'
            }

            // Meeting Types
            if (s.name && s.name.indexOf('Meeting Types') !== -1) {
              s.description = 'Track ' + s.placeholder + ' per ' + programLabel.toLowerCase() + ' and customize your list of ' + s.placeholder + '.'
            }
          })
        }
      }

      function save(vm, confirmed) {
        if (setting.endpoint) {
          //
          vm.api.forms.post(setting.endpoint, payload)
            .then(revertSettingOnError)
            .catch(revertSettingOnError)
        } else {
          //
          vm.api.forms.post(endpoints.settings.company.$, oldSettingsPayload)
            .then(revertSettingOnError)
            .catch(revertSettingOnError)
        }
      }

      function revertSettingOnError(r) {
        if (hide === false && setting.editing === true) {
          setting.name = setting.name + ' '
          setTimeout(() => {
            setting.name = setting.initial // setting.name.slice(0, -1)
            setting.editing = false
          })
        }

        vm.updateSettings(true)
        setting.saving = false

        setting.initial = setting.name.slice(0, -1)

        if (!r || r.name === 'Error') {
          setting.value = !setting.value
        }
      }
    },

    updateSettings(refresh) {
      if (typeof refresh === 'object') {
        this.$nextTick(() => {
          global.localStorage.setItem('evedUserSettings', JSON.stringify(refresh))
        })
        return true
      }

      const persistentSettings = refresh === true
                                  ? null
                                  : JSON.parse(global.localStorage.getItem('evedUserSettings'))

      this.$user.$.companySettings = refresh === true
                                      ? this.settings
                                      : persistentSettings || init()
      if (!persistentSettings) {
        // persist settings
        global.localStorage.setItem('evedUserSettings', JSON.stringify(this.$user.$.companySettings))
      } else {
        // default settings
        init()
      }

      // update vm
      // if (!refresh) {
      if (!this.$user.$.settings) { this.$user.$.settings = { company: null } }

      this.$user.$.settings.company = persistentSettings &&
                                      persistentSettings.length &&
                                      persistentSettings ||
                                      this.$user.$.settings.company ||
                                      cards

      this.settings = this.$user.$.settings.company.map(k => {
        k.value = k.value || this.$user.$.companySettings[k.status]
        k.saving = false
        k.editable = k.editable || false
        k.editing = false
        return k
      })
      // }

      return persistentSettings
    }
  }
}
</script>

<!-- SETTINGS CARD TEMPLATE -->
<template lang="pug">
.content
  ed-title-nav(:title='$t("Company Settings")')
  .container-fluid
    ed-section.active(offset=1)
      span(v-for='(setting, index) in settings')
        ed-card(
          :key='setting.name'
          :title='setting.name'
          :title-tooltip='setting.editable || null'
          :subtitle='subtitle(setting)'
          :footer-button='setting.configurable'
          :icon='setting.icon'
          :class='{ "new": setting.new, "editable": setting.editable }'
          :endpoint='setting.saving && (setting.endpoint || api.$.settings.company.$)'
          v-if='typeof setting.condition === "undefined" || setting.condition'
          @edit='edit(setting)'
        )
          span(v-if='setting.help' slot='mask') {{ setting.help }}
          ed-switch(slot='top'
                    v-if='setting.status !== null'
                    :value='$user.$.companySettings[setting.status]'
                    :disabled='setting.pending'
                    v-model='settings[index].value'
                    @input='change(setting)'
                    @mousedown='handleEndpoint(setting, $event)'
                    :emit-on-mount='false'
          )
          span.icon.-pull-left(v-if='setting.editable' slot='title-icon' @click='edit(setting)')
            | &nbsp;&nbsp;
            i.edit-icon.fas.fa-pen(v-tooltip.bottom='setting.editable || null')
          ed-input.edit-field.small.inline(update-button
                                :disabled='isDisabled(setting)'
                                autofocus
                                transition='fade'
                                v-if='setting.editable && setting.editing'
                                v-model='setting.name'
                                :maxlength='25'
                                @update='change(setting, false)'
                                @mousedown='handleEndpoint(setting, $event)'
                                v-on-clickaway='() => edit(setting, false)'
                                slot='title'
          )
          div(slot='footer')
            ed-button.btn.btn-actionbar-(@click='edit'
              :href='backUrl(setting.url, setting)'
              v-if='setting.configurable != "false"'
              :disabled='isDisabled(setting)'
              button-class='btn btn-actionbar-'
            ) {{ setting.configurable }}
</template>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";

.dawn-card {
  &.editable {
    >>> .title-text {
      overflow: hidden;
      cursor: pointer;
      max-width: 68%;
      display: inline-block;
      white-space: nowrap;
      text-overflow: ellipsis;
      vertical-align: bottom;
    }

    >>> .edit-field {
      max-width: 70%;
      .form-control {
        position: relative;
        top: 1px;
        left: -2px;
        width: 100%;
        padding-right: 35px;
      }
    }

    >>> .update-button + .icon {
      display: none;
    }

    >>> .title {
      &:hover {
        .edit-icon {
          color: #000;
        }
      }
    }
  }
}

.edit-icon, .edit-field {
  cursor: pointer;
}

.edit-icon {
  @include theme {
  color: v(background);
  }
}

.fade-transition {
  transition: 1s all ease;
  opacity: 1;
}
.fade-enter, .fade-leave {
  opacity: 0;
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/Admin/settings.vue